import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import toast from 'react-hot-toast';
import {
  Bell, Mail, MessageSquare, Smartphone, Clock, ArrowLeft, Save
} from 'lucide-react';

const channelMeta = {
  email: { icon: Mail, label: 'Email', desc: 'Receive notifications via email' },
  sms: { icon: MessageSquare, label: 'SMS', desc: 'Receive text message alerts' },
  push: { icon: Smartphone, label: 'Push', desc: 'In-app and browser push notifications' },
};

const Toggle = ({ checked, onChange, disabled }) => (
  <button
    onClick={() => !disabled && onChange(!checked)}
    disabled={disabled}
    className={`relative w-11 h-6 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary/30 ${
      checked ? 'bg-primary' : 'bg-slate-300'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
  >
    <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${checked ? 'translate-x-5' : 'translate-x-0'}`} />
  </button>
);

const NotificationPreferences = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [channels, setChannels] = useState({ email: true, sms: false, push: true });
  const [quietHours, setQuietHours] = useState({ enabled: false, start: '22:00', end: '08:00' });

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/profile/notifications');
        const data = res?.data?.data;
        if (data?.channels) setChannels(data.channels);
        if (data?.quietHours) setQuietHours(data.quietHours);
      } catch {
        // Use defaults
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/profile/notifications', { channels, quietHours });
      toast.success('Notification preferences saved');
    } catch {
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4 py-6">
        {[1, 2, 3].map(i => <div key={i} className="h-20 rounded-2xl animate-pulse bg-slate-100" />)}
      </div>
    );
  }

  return (
    <div className="space-y-6 py-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-2 rounded-xl hover:bg-slate-100 transition-all">
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <div>
          <h2 className="font-headline text-2xl font-extrabold text-slate-900 tracking-tight">Notification Preferences</h2>
          <p className="text-slate-500 text-sm mt-1">Choose how and when you receive notifications</p>
        </div>
      </div>

      {/* Channels */}
      <div className="bg-white rounded-2xl shadow-sm border border-outline-variant/10 overflow-hidden">
        <div className="p-5 border-b border-slate-100">
          <h3 className="font-bold text-lg text-slate-900 flex items-center gap-2">
            <Bell className="w-5 h-5 text-primary" /> Notification Channels
          </h3>
          <p className="text-xs text-slate-500 mt-1">Enable or disable notification delivery methods</p>
        </div>
        <div className="divide-y divide-slate-100">
          {Object.entries(channelMeta).map(([key, meta]) => {
            const Icon = meta.icon;
            return (
              <div key={key} className="flex items-center justify-between p-5">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-900">{meta.label}</p>
                    <p className="text-xs text-slate-500">{meta.desc}</p>
                  </div>
                </div>
                <Toggle
                  checked={channels[key] !== false}
                  onChange={(val) => setChannels(prev => ({ ...prev, [key]: val }))}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Quiet Hours */}
      <div className="bg-white rounded-2xl shadow-sm border border-outline-variant/10 overflow-hidden">
        <div className="p-5 border-b border-slate-100">
          <h3 className="font-bold text-lg text-slate-900 flex items-center gap-2">
            <Clock className="w-5 h-5 text-primary" /> Quiet Hours
          </h3>
          <p className="text-xs text-slate-500 mt-1">Suppress non-urgent notifications during these hours</p>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-bold text-slate-900">Enable Quiet Hours</p>
              <p className="text-xs text-slate-500">Urgent notifications will still come through</p>
            </div>
            <Toggle
              checked={quietHours.enabled}
              onChange={(val) => setQuietHours(prev => ({ ...prev, enabled: val }))}
            />
          </div>
          {quietHours.enabled && (
            <div className="flex items-center gap-4 pt-2">
              <div className="flex-1">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Start</label>
                <input
                  type="time"
                  value={quietHours.start}
                  onChange={(e) => setQuietHours(prev => ({ ...prev, start: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              <div className="flex-1">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">End</label>
                <input
                  type="time"
                  value={quietHours.end}
                  onChange={(e) => setQuietHours(prev => ({ ...prev, end: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Save */}
      <button
        onClick={handleSave}
        disabled={saving}
        className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-primary text-white rounded-2xl font-bold text-sm shadow-md hover:shadow-lg active:scale-95 transition-all disabled:opacity-50"
      >
        <Save className="w-4 h-4" />
        {saving ? 'Saving...' : 'Save Preferences'}
      </button>
    </div>
  );
};

export default NotificationPreferences;
